import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getDifficultyLabel } from '../../utils/constants';

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  strength: 'Strength',
  core_strength: 'Core Strength'
};

const experienceLevels = ['beginner', 'intermediate', 'advanced'];

export const EditUserModal = ({ isOpen, user, onClose, onSave, saving, onChange }) => {
  if (!isOpen || !user) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card border border-border p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="edit-user-modal"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-xl text-white uppercase tracking-wider">
              Edit <span className="text-primary">User</span>
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Name</Label>
                <Input
                  value={user.name || ''}
                  onChange={(e) => onChange({ ...user, name: e.target.value })}
                  className="bg-black border-border text-white"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Email</Label>
                <Input
                  value={user.email || ''}
                  onChange={(e) => onChange({ ...user, email: e.target.value })}
                  className="bg-black border-border text-white"
                  data-testid="edit-user-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Age</Label>
                <Input
                  type="number"
                  value={user.age || ''}
                  onChange={(e) => onChange({ ...user, age: parseInt(e.target.value) || null })}
                  className="bg-black border-border text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Weight (lbs)</Label>
                <Input
                  type="number"
                  value={user.weight || ''}
                  onChange={(e) => onChange({ ...user, weight: parseFloat(e.target.value) || null })}
                  className="bg-black border-border text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Height (in)</Label>
                <Input
                  type="number"
                  value={user.height || ''}
                  onChange={(e) => onChange({ ...user, height: parseFloat(e.target.value) || null })}
                  className="bg-black border-border text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm">Experience Level</Label>
              <div className="flex gap-2">
                {experienceLevels.map(level => (
                  <Button
                    key={level}
                    type="button"
                    variant={user.experience_level === level ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onChange({ ...user, experience_level: level })}
                    className={user.experience_level === level 
                      ? 'bg-primary text-black' 
                      : 'border-border text-white hover:bg-white/10'}
                  >
                    {getDifficultyLabel(level)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm">Fitness Goals</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(goalLabels).map(([key, label]) => (
                  <Button
                    key={key}
                    type="button"
                    variant={(user.fitness_goals || []).includes(key) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const goals = user.fitness_goals || [];
                      const newGoals = goals.includes(key)
                        ? goals.filter(g => g !== key)
                        : [...goals, key];
                      onChange({ ...user, fitness_goals: newGoals });
                    }}
                    className={(user.fitness_goals || []).includes(key)
                      ? 'bg-primary text-black'
                      : 'border-border text-white hover:bg-white/10'}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t border-border">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={user.onboarding_completed || false}
                  onChange={(e) => onChange({ ...user, onboarding_completed: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-white">Onboarding Completed</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={user.is_admin || false}
                  onChange={(e) => onChange({ ...user, is_admin: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-white">Admin Access</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-border text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid="save-user-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
